import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Pause, Play, Timer } from 'lucide-react'
import apiClient from '@/api/client'
import type { OnlineOrderingStatus, SetOnlineOrderingStatusRequest } from '@/types'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toastHelpers } from '@/lib/toast-helpers'
import { BUSY_DURATION_MIN, BUSY_OPTIONS, PAUSE_OPTIONS, clockTime } from '@/lib/onlineOrderTiming'

// F-ONL-N3: pause / busy for online ordering, from the board.
//
// Shared with AdminOnlineOrders' SSE handler: a change made on another screen
// streams `online_ordering_status` and every board repaints.
export const ORDERING_STATUS_QUERY_KEY = ['online-orders', 'ordering-status'] as const

const OPEN: OnlineOrderingStatus = { mode: 'open', unanswered_expire_minutes: 0 }

/**
 * The state in force. A timed pause or busy spell lifts itself on the server;
 * the board re-checks the clock every 30s so it stops saying "Paused" the
 * moment the time is up rather than at the next poll.
 */
export function useOrderingStatus(enabled = true) {
  const query = useQuery({
    queryKey: ORDERING_STATUS_QUERY_KEY,
    queryFn: () => apiClient.getOnlineOrderingStatus().then((r) => r.data ?? OPEN),
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: 'always',
    staleTime: 0,
  })
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(id)
  }, [])
  const raw = query.data ?? OPEN
  const expired = raw.mode !== 'open' && !!raw.until && new Date(raw.until).getTime() <= now
  const status: OnlineOrderingStatus = expired ? { ...OPEN, unanswered_expire_minutes: raw.unanswered_expire_minutes } : raw
  return { status, isLoading: query.isLoading }
}

export function useSetOrderingStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: SetOnlineOrderingStatusRequest) => apiClient.setOnlineOrderingStatus(body),
    onSuccess: (res) => {
      if (res.data) queryClient.setQueryData(ORDERING_STATUS_QUERY_KEY, res.data)
      // Busy changes the "ready in" each Incoming card suggests.
      queryClient.invalidateQueries({ queryKey: ['online-orders', 'pending'] })
      toastHelpers.success(res.message || 'Online ordering updated')
    },
    onError: (err: unknown) => {
      const e = err as { message?: string }
      toastHelpers.error('Could not change online ordering', e?.message || 'Please try again.')
    },
  })
}

/** "Paused until 8:30 PM" / "Busy +15 min until 9:10 PM". */
export function orderingStatusSummary(s: OnlineOrderingStatus): string {
  const until = s.until ? ` until ${clockTime(s.until)}` : ''
  if (s.mode === 'paused') return `Paused${until}`
  if (s.mode === 'busy') return `Busy +${s.extra_minutes ?? 0} min${until}`
  return 'Taking orders'
}

/** Header switch: shows the state, opens the menu to change it. */
export function OrderingStatusControl({ status }: { status: OnlineOrderingStatus }) {
  const setStatus = useSetOrderingStatus()
  const dot =
    status.mode === 'paused' ? 'bg-red-500' : status.mode === 'busy' ? 'bg-amber-500' : 'bg-emerald-500'
  const short =
    status.mode === 'paused'
      ? status.until
        ? `Paused · ${clockTime(status.until)}`
        : 'Paused'
      : status.mode === 'busy'
        ? `Busy +${status.extra_minutes ?? 0}m`
        : 'Taking orders'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2"
          disabled={setStatus.isPending}
          aria-label={`Online ordering: ${orderingStatusSummary(status)}. Change`}
        >
          <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden="true" />
          <span className="whitespace-nowrap">{short}</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {orderingStatusSummary(status)}
          {status.mode !== 'open' && status.set_by ? ` — set by ${status.set_by}` : ''}
        </DropdownMenuLabel>
        {status.mode !== 'open' && (
          <DropdownMenuItem onSelect={() => setStatus.mutate({ mode: 'open' })}>
            <Play className="mr-2 h-4 w-4 text-emerald-600" />
            Take orders normally
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Pause new orders</DropdownMenuLabel>
        {PAUSE_OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.label}
            onSelect={() => setStatus.mutate({ mode: 'paused', minutes: o.minutes })}
          >
            <Pause className="mr-2 h-4 w-4 text-red-600" />
            {o.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Busy — add time to every quote</DropdownMenuLabel>
        {BUSY_OPTIONS.map((m) => (
          <DropdownMenuItem
            key={m}
            onSelect={() => setStatus.mutate({ mode: 'busy', extra_minutes: m, minutes: BUSY_DURATION_MIN })}
          >
            <Timer className="mr-2 h-4 w-4 text-amber-600" />+{m} min for the next hour
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Full-width strip while ordering is paused or busy, so nobody forgets the
 * shop is shut. Resuming is one tap from here.
 */
export function OrderingStatusBanner({ status }: { status: OnlineOrderingStatus }) {
  const setStatus = useSetOrderingStatus()
  if (status.mode === 'open') return null
  const paused = status.mode === 'paused'
  const until = status.until ? ` until ${clockTime(status.until)}` : ''
  return (
    <div
      role="status"
      className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-sm ${
        paused ? 'bg-red-600 text-white' : 'bg-warning-surface text-warning-fg'
      }`}
    >
      <span className="font-semibold">
        {paused ? `Online ordering is paused${until}.` : `Busy: every quoted time is +${status.extra_minutes ?? 0} min${until}.`}
      </span>
      <span className={paused ? 'text-white/90' : ''}>
        {paused
          ? "Customers can't place pickup or delivery orders. Dine-in QR orders still come through."
          : 'New orders still come in; customers see the longer time.'}
      </span>
      <Button
        size="sm"
        variant={paused ? 'secondary' : 'outline'}
        className="h-8"
        disabled={setStatus.isPending}
        onClick={() => setStatus.mutate({ mode: 'open' })}
      >
        <Play className="mr-1.5 h-4 w-4" />
        {paused ? 'Resume now' : 'Back to normal'}
      </Button>
    </div>
  )
}
